import type {
  ClinicDateFormat,
  ClinicNumberFormat,
  ClinicTimeFormat,
} from '@/shared/session/types'

import type { Translate } from './locale'

export const DEFAULT_CLINIC_DATE_FORMAT: ClinicDateFormat = 'day-month-year'
export const DEFAULT_CLINIC_TIME_FORMAT: ClinicTimeFormat = '24-hour'
export const DEFAULT_CLINIC_NUMBER_FORMAT: ClinicNumberFormat = 'decimal-dot-group-comma'

const BYTES_PER_KIB = 1024
const BYTES_PER_MIB = BYTES_PER_KIB * BYTES_PER_KIB

/* How far each size tier is spelled out. A whole count of bytes or kilobytes
   is as fine as anyone reads a file listing; a megabyte is coarse enough that
   the first decimal still tells two files apart. */
const BYTE_DIGITS = 0
const KILOBYTE_DIGITS = 0
const MEGABYTE_DIGITS = 1

export interface ClinicTemporalPreferences {
  dateFormat: ClinicDateFormat
  timeFormat: ClinicTimeFormat
}

export interface ClinicFormatPreferences extends ClinicTemporalPreferences {
  numberFormat: ClinicNumberFormat
}

interface NumberSeparators {
  group: string
  decimal: string
}

export function formatClinicDate(
  date: Date,
  locale: string,
  dateFormat: ClinicDateFormat,
  timeZone = 'UTC',
): string {
  const { year, month, day } = numericDateParts(date, locale, timeZone)
  if (dateFormat === 'month-day-year') return `${month}/${day}/${year}`
  if (dateFormat === 'year-month-day') return `${year}-${month}-${day}`
  return `${day}/${month}/${year}`
}

export function formatClinicTime(
  date: Date,
  locale: string,
  timeFormat: ClinicTimeFormat,
  timeZone = 'UTC',
): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: timeFormat === '12-hour' ? 'h12' : 'h23',
    timeZone,
  }).format(date)
}

const MINUTES_PER_DAY = 24 * 60
const SECONDS_PER_MINUTE = 60

/**
 * A MINUTE OF THE CIVIL DAY, AS THE CLINIC'S CLOCK SHOWS IT.
 *
 * The schedule assembles clocks from minutes rather than from instants — a
 * ruler hour, a band's accessible name, the range an interval dialog says back
 * — and every one of them was `String(h).padStart(2, '0') + ':' + …`, which is
 * a second implementation of the clinic's hour cycle that always answers
 * 24-hour. A clinic set to 12-hour read appointment cards at `2:30 PM` with the
 * ruler beside them at `14:00`, on one screen. The formatting rule could not
 * see it: a hand-padded clock builds no `Intl` formatter.
 *
 * The minute is taken MODULO the day, so the canvas's own bottom edge — minute
 * 1440, which a 24-hour clock can spell `24:00` and a 12-hour clock cannot
 * spell at all — reads as the midnight it is. That is the reading the create
 * dialog already chose for the same edge; the rest of the board now agrees
 * with it rather than each surface deciding for itself.
 */
export function formatClinicClock(
  minuteOfDay: number,
  locale: string,
  timeFormat: ClinicTimeFormat,
): string {
  const minute = Math.round(minuteOfDay)
  const inDay = ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  return formatClinicTime(new Date(Date.UTC(2000, 0, 1, 0, inDay)), locale, timeFormat, 'UTC')
}

/**
 * AN ELAPSED DURATION AS `MM:SS`, which is not a clock and has no preference.
 *
 * A recording that has run for 90 seconds is `01:30` in a 12-hour clinic and in
 * a 24-hour one; what the clinic decides is how an hour of the DAY is spelled,
 * not how long something has been going. It lives here anyway because the
 * digits are the locale's — a script with its own numerals writes a stopwatch
 * in them — and because the rule that forbids a hand-padded clock cannot make
 * an exception for the one that happens to be counting.
 */
export function formatClinicDuration(seconds: number, locale: string): string {
  const whole = Math.max(0, Math.floor(seconds))
  const digits = new Intl.NumberFormat(locale, { minimumIntegerDigits: 2, useGrouping: false })
  return `${digits.format(Math.floor(whole / SECONDS_PER_MINUTE))}:${digits.format(whole % SECONDS_PER_MINUTE)}`
}

export function formatClinicDateTime(
  date: Date,
  locale: string,
  timeZone: string,
  preferences: ClinicTemporalPreferences,
): string {
  return `${formatClinicDate(date, locale, preferences.dateFormat, timeZone)} ${formatClinicTime(
    date,
    locale,
    preferences.timeFormat,
    timeZone,
  )}`
}

/**
 * A DISPLAYED INSTANT, IN THE TWO PARTS IT IS MADE OF.
 *
 * A registry column is rarely wide enough for "13/08/2026 12:05" on one line,
 * and a timestamp cut in the middle states a different day than the one that
 * was recorded. A cell that stacks the day over the clock needs the two apart,
 * and WHERE the boundary is stays this module's business: a clinic reading its
 * dates `year-month-day` puts it somewhere else, so a caller that split the
 * formatted string would be reimplementing the preference it was given.
 */
export interface ClinicInstantParts {
  day: string
  /** Empty when the value was not an instant — see below. */
  time: string
}

/**
 * The parts `formatClinicInstant` joins.
 *
 * A value the server did not state as a timestamp comes back whole as the day
 * with no clock beside it, which is the same answer the joined form gives: a
 * broken value reads identically wherever it lands, rather than turning into a
 * plausible date in one column and staying raw in the next.
 */
export function formatClinicInstantParts(
  instant: string,
  locale: string,
  timeZone: string,
  preferences: ClinicTemporalPreferences,
): ClinicInstantParts {
  const date = new Date(instant)
  if (Number.isNaN(date.getTime())) {
    return { day: instant, time: '' }
  }
  return {
    day: formatClinicDate(date, locale, preferences.dateFormat, timeZone),
    time: formatClinicTime(date, locale, preferences.timeFormat, timeZone),
  }
}

export function formatClinicInstant(
  instant: string,
  locale: string,
  timeZone: string,
  preferences: ClinicTemporalPreferences,
): string {
  const { day, time } = formatClinicInstantParts(instant, locale, timeZone, preferences)
  return time === '' ? day : `${day} ${time}`
}

export function formatClinicCivilDay(
  dayIso: string,
  locale: string,
  dateFormat: ClinicDateFormat,
): string {
  const date = civilDayDate(dayIso)
  return date ? formatClinicDate(date, locale, dateFormat) : ''
}

/**
 * A civil date a person stated as far as they knew it: a year, a year and a
 * month, or a whole day. A residence is dated `2015-03` as often as not, and
 * printing the stored text raw put an ISO fragment beside dates written the
 * clinic's way. The month-and-year follows the clinic's own order; anything
 * that is none of the three shapes is returned as stored rather than guessed.
 */
export function formatClinicPartialCivilDay(
  value: string,
  locale: string,
  dateFormat: ClinicDateFormat,
): string {
  if (/^\d{4}$/.test(value)) {
    return value
  }
  const month = /^(\d{4})-(\d{2})$/.exec(value)
  if (month) {
    return dateFormat === 'year-month-day' ? `${month[1]}-${month[2]}` : `${month[2]}/${month[1]}`
  }
  return formatClinicCivilDay(value, locale, dateFormat) || value
}

export function formatClinicFullCivilDay(dayIso: string, locale: string): string {
  const date = civilDayDate(dayIso)
  if (!date) return ''
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'full',
    timeZone: 'UTC',
  }).format(date)
}

export function formatClinicDayMonth(
  date: Date,
  locale: string,
  dateFormat: ClinicDateFormat,
  timeZone = 'UTC',
): string {
  const { month, day } = numericDateParts(date, locale, timeZone)
  if (dateFormat === 'month-day-year') return `${month}/${day}`
  if (dateFormat === 'year-month-day') return `${month}-${day}`
  return `${day}/${month}`
}

export function formatClinicMonthName(date: Date, locale: string, timeZone = 'UTC'): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', timeZone }).format(date)
}

export function formatClinicMonthYear(date: Date, locale: string, timeZone = 'UTC'): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone }).format(date)
}

export function formatClinicShortMonthDate(date: Date, locale: string, timeZone = 'UTC'): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(date)
}

export function formatClinicWeekdayLabels(
  dates: readonly Date[],
  locale: string,
  style: 'long' | 'short' | 'narrow',
  timeZone = 'UTC',
): string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: style, timeZone })
  return dates.map((date) => format.format(date))
}

/**
 * A RUN OF FACTS ABOUT ONE THING, not an enumeration of several things.
 *
 * A practitioner's position and specialty, an indicator's kind, unit and
 * reference range, an event's name and state: reading them out with "and"
 * claims a relation the facts do not have, and the middle dot they used to be
 * joined with is a word to a screen reader and the same mark in every language.
 *
 * `narrow` + `conjunction` is CLDR's shortest enumerating pattern and the only
 * one that never collapses to a bare space: English and Russian give plain
 * commas, and Hebrew gives the connector Hebrew actually uses. `unit` reads as
 * the right idea and renders `A B C` in Russian, which for words rather than
 * measurements is two facts welded into one.
 */
export const FACT_RUN: Intl.ListFormatOptions = { style: 'narrow', type: 'conjunction' }

/**
 * SEVERAL THINGS NAMED AS ONE PHRASE, in the reader's language.
 *
 * `join(', ')` is a comma in every language until somebody notices it is wrong
 * in one, and it is wrong in more than one: the connector, the spacing and
 * whether the last item takes a word of its own are facts about the language,
 * and `Intl` already holds all three. A hand-written comma also gives assistive
 * technology a run-on line where the reader wanted a list.
 *
 * The options are passed through rather than fixed, because the LIST TYPE is a
 * fact about the sentence and only the caller knows it: resources assigned to
 * an appointment are all of them (`conjunction`), the columns a search matched
 * are any of them (`disjunction`). The default is the common case; a caller
 * that means "or" says so.
 *
 * Empty and single-item lists are `Intl`'s own answers — '' and the item — so
 * no caller needs a guard, and none may keep a second one.
 */
export function formatClinicList(
  items: readonly string[],
  locale: string,
  options: Intl.ListFormatOptions = {},
): string {
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction', ...options }).format([
    ...items,
  ])
}

export function formatClinicNumber(
  value: number,
  numberFormat: ClinicNumberFormat,
  options: Intl.NumberFormatOptions = {},
): string {
  if (!Number.isFinite(value)) {
    return ''
  }
  return withClinicSeparators(new Intl.NumberFormat('en-US', options).formatToParts(value), numberFormat)
}

/** A currency as ISO 4217 writes one: exactly three letters. */
const CURRENCY_CODE = /^[A-Za-z]{3}$/u

/**
 * MONEY, IN THE CURRENCY THE SERVER STATED AND THE MARKS THE CLINIC CHOSE.
 *
 * The product wrote `1,234.56 ILS`: a Latin code welded onto the number in
 * every language, for whatever currency the wire was actually carrying. Hebrew
 * and Russian both name that currency ₪ and both put it after the amount,
 * English puts it in front, and the day a second currency reaches a screen the
 * suffix would have named the wrong one on all of them at once.
 *
 * THE TWO HALVES COME FROM DIFFERENT PLACES, and that is the shape of this
 * function. WHICH mark separates the thousands is the clinic's settings, as it
 * is for every other number the product prints — a clinic reading `1 234,56`
 * in its ledger does not read `1,234.56` in its invoices. WHERE the currency
 * sits and which glyph names it is the reader's language, which is CLDR's
 * answer and not ours to invent, down to the bidi marks Hebrew needs to keep
 * the symbol on the correct side of the digits.
 */
export function formatClinicMoney(
  amount: number,
  currency: string,
  locale: string,
  numberFormat: ClinicNumberFormat,
): string {
  if (!Number.isFinite(amount)) {
    return ''
  }
  if (!CURRENCY_CODE.test(currency)) {
    /* `Intl` throws on a code that is not three letters, and one malformed row
       may not take the registry it sits in down with it. The amount beside the
       code as given is the last honest thing there is to say about it. */
    return `${formatClinicNumber(amount, numberFormat, MINOR_UNIT_DIGITS)} ${currency}`.trim()
  }
  return withClinicSeparators(
    new Intl.NumberFormat(locale, { style: 'currency', currency }).formatToParts(amount),
    numberFormat,
  )
}

/** What a hundredths-based amount reads as when its currency cannot be named. */
const MINOR_UNIT_DIGITS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}

/**
 * Money from exact decimal digits, where `formatClinicMoney` cannot go: a
 * `number` is exact only to 2^53 and the Finance contract's int64 amounts
 * exceed it, so this variant takes a canonical decimal STRING and never
 * converts it — the integer half is formatted as a BigInt and the fraction
 * digits are spliced in as given. The fraction width is the string's own:
 * the currency's scale is the SERVER's fact, and CLDR's per-currency digit
 * table is deliberately not consulted. Currency glyph and placement stay
 * CLDR's answer exactly as above, the clinic's marks are substituted the
 * same single way, and the spliced digits are Latin, matching every locale
 * this product ships.
 */
export function formatClinicMoneyExact(
  amount: string,
  currency: string,
  locale: string,
  numberFormat: ClinicNumberFormat,
): string {
  const exact = exactAmount(amount)
  if (!exact) {
    return ''
  }
  const width: Intl.NumberFormatOptions = {
    minimumFractionDigits: exact.fraction.length,
    maximumFractionDigits: exact.fraction.length,
  }
  if (!CURRENCY_CODE.test(currency)) {
    /* Same fallback as `formatClinicMoney`: the exact amount beside a code
       `Intl` would throw on. */
    return `${exactNumber(exact, 'en-US', width, numberFormat)} ${currency}`.trim()
  }
  return exactNumber(exact, locale, { style: 'currency', currency, ...width }, numberFormat)
}

/**
 * The clinic's decimal mark, stated once for input fields and their parsers
 * so no caller decides it locally.
 */
export function clinicDecimalSeparator(numberFormat: ClinicNumberFormat): string {
  return numberSeparators(numberFormat).decimal
}

/** An exact decimal amount, split into the digits `Intl` must not rework. */
interface ExactAmount {
  negative: boolean
  integer: string
  fraction: string
}

/**
 * Canonical exact-decimal spelling: no plus, exponent, grouping or padded
 * integer part. `Intl` refuses an explicit fraction width beyond 20.
 */
const EXACT_DECIMAL = /^(-?)(0|[1-9]\d*)(?:\.(\d{1,20}))?$/

/* Resource bound checked before the regex runs: a sign, a dot, 20 fraction
   digits and any honest integer part fit well inside 64 characters. */
const MAX_EXACT_AMOUNT_LENGTH = 64

function exactAmount(amount: string): ExactAmount | null {
  if (amount.length > MAX_EXACT_AMOUNT_LENGTH) {
    return null
  }
  const match = EXACT_DECIMAL.exec(amount)
  if (!match || match[2] === undefined) {
    return null
  }
  const integer = match[2]
  const fraction = match[3] ?? ''
  // A sign is real only while some digit behind it is: `-0.00` never prints.
  const negative = match[1] === '-' && (integer !== '0' || /[1-9]/.test(fraction))
  return { negative, integer, fraction }
}

/**
 * The exact digits through the one formatter: the integer half goes in as a
 * BigInt so grouping and sign/currency placement are CLDR's answer for the
 * true magnitude, then the caller's fraction digits replace the formatter's
 * zero padding. A negative amount whose integer part is zero rides a carrier
 * of one — BigInt has no minus zero to carry the sign — and the carrier's
 * single integer digit is put back to zero in the parts.
 */
function exactNumber(
  exact: ExactAmount,
  locale: string,
  options: Intl.NumberFormatOptions,
  numberFormat: ClinicNumberFormat,
): string {
  const zeroCarrier = exact.negative && exact.integer === '0'
  const carrier = BigInt(`${exact.negative ? '-' : ''}${zeroCarrier ? '1' : exact.integer}`)
  const parts = new Intl.NumberFormat(locale, options)
    .formatToParts(carrier)
    .map((part) => {
      if (part.type === 'fraction') return { ...part, value: exact.fraction }
      if (part.type === 'integer' && zeroCarrier) return { ...part, value: '0' }
      return part
    })
  return withClinicSeparators(parts, numberFormat)
}

/**
 * The clinic's own marks, substituted into a formatter's parts.
 *
 * Shared by the plain and the money renderers because it is one fact: a
 * separator the clinic chose. Formatting the money number a second way here
 * would be the drift this module exists to end.
 */
function withClinicSeparators(
  parts: readonly Intl.NumberFormatPart[],
  numberFormat: ClinicNumberFormat,
): string {
  const separators = numberSeparators(numberFormat)
  return parts
    .map((part) => {
      if (part.type === 'group') return separators.group
      if (part.type === 'decimal') return separators.decimal
      return part.value
    })
    .join('')
}

/**
 * A FILE SIZE, IN THE UNIT A PERSON WOULD SAY IT IN.
 *
 * There were two of these, and they disagreed: one stepped up to megabytes and
 * the other never left kilobytes, so a 40 MiB scan read "40.0 MB" in Patient
 * Files and "40960 KB" in the Control Center table beside it. The tiers belong
 * to one function for the same reason the clinic's number separators do — a
 * size is a size wherever it is printed.
 *
 * THE UNIT IS CATALOGUE COPY, not a Latin literal glued to the number. The
 * Russian catalogue already abbreviates these units in its own script in the
 * size filter one column away; a helper that concatenated "KB" made the row
 * disagree with the control that filters it. The number goes in as a
 * parameter, so a locale that puts its unit first can.
 *
 * The steps are binary, matching every byte count the server states.
 */
export function formatClinicByteSize(
  bytes: number,
  numberFormat: ClinicNumberFormat,
  t: Translate,
): string {
  if (!Number.isFinite(bytes)) {
    // A bare unit with no number beside it names nothing; the empty cell is
    // the honest reading of a size the server did not state.
    return ''
  }
  /*
   * THE UNIT IS CHOSEN FROM THE NUMBER THE READER WILL SEE, not from the raw
   * count. Comparing the count against the tier alone kept a 1,048,575-byte
   * file — one byte short of a mebibyte — in the kilobyte tier, where rounding
   * to a whole number printed `1024 KB` directly above a file one byte larger
   * reading `1 MB`. A size that names its own next unit is a size nobody can
   * compare against the row beside it.
   */
  const kilobytes = bytes / BYTES_PER_KIB
  if (rounded(kilobytes, KILOBYTE_DIGITS) >= BYTES_PER_KIB) {
    return t('common.megabytes', {
      size: formatClinicNumber(bytes / BYTES_PER_MIB, numberFormat, {
        maximumFractionDigits: MEGABYTE_DIGITS,
      }),
    })
  }
  if (rounded(bytes, BYTE_DIGITS) >= BYTES_PER_KIB) {
    return t('common.kilobytes', {
      size: formatClinicNumber(kilobytes, numberFormat, {
        maximumFractionDigits: KILOBYTE_DIGITS,
      }),
    })
  }
  return t('common.bytes', {
    size: formatClinicNumber(bytes, numberFormat, { maximumFractionDigits: BYTE_DIGITS }),
  })
}

/**
 * What the number will read as once printed to that many places, so the tier
 * above can be asked about the printed value rather than the exact one. The
 * formatter rounds half away from zero and so does this.
 */
function rounded(value: number, digits: number): number {
  return Number(value.toFixed(digits))
}

function numberSeparators(numberFormat: ClinicNumberFormat): NumberSeparators {
  if (numberFormat === 'decimal-comma-group-space') {
    return { group: ' ', decimal: ',' }
  }
  if (numberFormat === 'decimal-comma-group-dot') {
    return { group: '.', decimal: ',' }
  }
  return { group: ',', decimal: '.' }
}

function numericDateParts(
  date: Date,
  locale: string,
  timeZone: string,
): { year: string; month: string; day: string } {
  const values = new Map(
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  )
  return {
    year: values.get('year') ?? '',
    month: values.get('month') ?? '',
    day: values.get('day') ?? '',
  }
}

function civilDayDate(dayIso: string): Date | null {
  const [year, month, day] = dayIso.split('-').map(Number)
  if (!year || !month || !day) {
    return null
  }
  return new Date(Date.UTC(year, month - 1, day, 12))
}
